// src/diagrams/contactFlowParsingUtils.js - Shared utilities for Contact Flow parsing

/**
 * Shared utilities for parsing Amazon Connect Contact Flow data
 * Used by both React Flow and Lucid Charts parsers
 */
export class ContactFlowParsingUtils {
  /**
   * Lookup flow name from ARN using exported data
   */
  static lookupFlowName(flowArn, exportedData, instanceId) {
    if (!flowArn) return 'Unknown Flow';
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for flow lookup');
      return this.extractIdFromArn(flowArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.contactFlows) {
      console.warn('No contactFlows found in exported data');
      return this.extractIdFromArn(flowArn);
    }
    
    const flowId = this.extractIdFromArn(flowArn);
    
    const flow = instanceData.contactFlows.find(f => f.Id === flowId);
    if (flow?.Name) {
      return flow.Name;
    } else {
      console.warn(`Flow not found, returning ID: ${flowId}`);
      return `Flow: ${flowId}`;
    }
  }

  /**
   * Lookup queue name from ARN using exported data
   */
  static lookupQueueName(queueArn, exportedData, instanceId) {
    if (!queueArn) return 'Unknown Queue';
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for queue lookup');
      return this.extractIdFromArn(queueArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.queues) {
      console.warn('No queues found in exported data');
      return this.extractIdFromArn(queueArn);
    }
    
    const queueId = this.extractIdFromArn(queueArn);
    
    const queue = instanceData.queues.find(q => q.Id === queueId);
    if (queue?.Name) {
      return queue.Name;
    } else {
      console.warn(`Queue not found, returning ID: ${queueId}`);
      return `Queue: ${queueId}`;
    }
  }

  /**
   * Lookup hours of operation name from ARN using exported data
   */
  static lookupHoursName(hoursArn, exportedData, instanceId) {
    if (!hoursArn) return 'Current Queue';
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for hours lookup');
      return this.extractIdFromArn(hoursArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.hoursOfOperation) {
      console.warn('No hoursOfOperation found in exported data');
      return this.extractIdFromArn(hoursArn);
    }
    
    const hoursId = this.extractIdFromArn(hoursArn);
    
    const hours = instanceData.hoursOfOperation.find(h => h.Id === hoursId);
    if (hours?.Name) {
      return hours.Name;
    } else {
      console.warn(`Hours not found, returning fallback: ${hoursId}`);
      return `Hours: ${hoursId}`;
    }
  }

  /**
   * Lookup prompt name from ARN using exported data
   */
  static lookupPromptName(promptArn, exportedData, instanceId) {
    if (!promptArn) return 'Unknown Prompt';
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for prompt lookup');
      return this.extractIdFromArn(promptArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.prompts) {
      console.warn('No prompts found in exported data');
      return this.extractIdFromArn(promptArn);
    }
    
    const promptId = this.extractIdFromArn(promptArn);
    
    const prompt = instanceData.prompts.find(p => p.Id === promptId);
    if (prompt?.Name) {
      return prompt.Name;
    } else {
      console.warn(`Prompt not found, returning ID: ${promptId}`);
      return `Prompt: ${promptId}`;
    }
  }

  /**
   * Lookup user name from ARN using exported data
   */
  static lookupUserName(userArn, exportedData, instanceId) {
    if (!userArn) return 'Unknown User';
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for user lookup');
      return this.extractIdFromArn(userArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.users) {
      console.warn('No users found in exported data');
      return this.extractIdFromArn(userArn);
    }
    
    const userId = this.extractIdFromArn(userArn);
    
    const user = instanceData.users.find(u => u.Id === userId);
    if (user?.Username) {
      return user.Username;
    } else {
      console.warn(`User not found, returning ID: ${userId}`);
      return `Agent: ${userId}`;
    }
  }

  /**
   * Lookup flow module name from ID using exported data
   */
  static lookupFlowModuleName(flowModuleId, exportedData, instanceId) {
    if (!flowModuleId) return 'Unknown Module';
    
    // Check if it's a JSONPath expression
    if (flowModuleId.startsWith('$.')) {
      return flowModuleId; // Display JSONPath as-is
    }
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for flow module lookup');
      return this.extractIdFromArn(flowModuleId);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.contactFlowModules) {
      console.warn('No contactFlowModules found in exported data');
      return this.extractIdFromArn(flowModuleId);
    }
    
    const moduleId = this.extractIdFromArn(flowModuleId);
    
    const module = instanceData.contactFlowModules.find(m => m.Id === moduleId);
    if (module?.Name) {
      return module.Name;
    } else {
      console.warn(`Flow module not found, returning ID: ${moduleId}`);
      return `Module: ${moduleId}`;
    }
  }

  /**
   * Lookup Lex V2 bot name and alias from ARN using exported data
   */
  static lookupLexV2BotName(botAliasArn, exportedData, instanceId) {
    if (!botAliasArn) return { name: 'Unknown Bot', alias: 'Unknown' };
    
    // Check if it's a JSONPath expression
    if (botAliasArn.startsWith('$.')) {
      return { name: botAliasArn, alias: 'Dynamic' };
    }
    
    if (!exportedData || !instanceId) {
      console.warn('Missing exportedData or instanceId for Lex V2 bot lookup');
      return this.extractLexV2InfoFromArn(botAliasArn);
    }
    
    const instanceData = exportedData[instanceId];
    if (!instanceData?.lexBotV2) {
      console.warn('No lexBotV2 found in exported data');
      return this.extractLexV2InfoFromArn(botAliasArn);
    }
    
    // Extract bot ID and alias ID from ARN
    // ARN format: arn:aws:lex:region:account:bot-alias/BOT_ID/ALIAS_ID
    const arnParts = botAliasArn.split('/');
    if (arnParts.length < 3) {
      console.warn(`Invalid Lex V2 bot alias ARN format: ${botAliasArn}`);
      return this.extractLexV2InfoFromArn(botAliasArn);
    }
    
    const botId = arnParts[arnParts.length - 2];
    const aliasId = arnParts[arnParts.length - 1];
    
    // Find the bot in the exported data
    const lexBot = instanceData.lexBotV2.find(bot => bot.botId === botId);
    if (lexBot) {
      // Find the alias
      const botAlias = lexBot.botAliases?.find(alias => alias.botAliasId === aliasId);
      return {
        name: lexBot.botName || 'Unknown Bot',
        alias: botAlias?.botAliasName || aliasId || 'Unknown Alias',
        botId: botId,
        aliasId: aliasId
      };
    } else {
      console.warn(`Lex V2 bot not found with ID: ${botId}`);
      return {
        name: `Bot: ${botId}`,
        alias: `Alias: ${aliasId}`,
        botId: botId,
        aliasId: aliasId
      };
    }
  }

  /**
   * Extract Lex V2 info from ARN when lookup fails
   */
  static extractLexV2InfoFromArn(botAliasArn) {
    if (!botAliasArn) return { name: 'Unknown Bot', alias: 'Unknown' };
    
    const arnParts = botAliasArn.split('/');
    if (arnParts.length >= 3) {
      const botId = arnParts[arnParts.length - 2];
      const aliasId = arnParts[arnParts.length - 1];
      return {
        name: `Bot: ${botId}`,
        alias: `Alias: ${aliasId}`,
        botId: botId,
        aliasId: aliasId
      };
    }
    
    return { name: 'Unknown Bot', alias: 'Unknown' };
  }

  /**
   * Extract ID from ARN
   */
  static extractIdFromArn(arn) {
    if (!arn) return 'Unknown';
    const parts = arn.split('/');
    return parts[parts.length - 1] || arn;
  }

  /**
   * Extract Lambda function name from ARN
   */
  static extractLambdaName(functionArn) {
    if (!functionArn) return 'Unknown';
    const parts = functionArn.split(':');
    return parts[parts.length - 1] || 'Lambda';
  }

  /**
   * Truncate text to specified length
   */
  static truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Format action type for display
   */
  static formatActionType(type) {
    return type.replace(/([A-Z])/g, ' $1').trim();
  }

  /**
   * Get error label for display
   */
  static getErrorLabel(errorType) {
    const errorLabels = {
      'NoMatchingError': 'Error',
      'NoMatchingCondition': 'No Match',
      'NoMatch': 'No Match',
      'CallbackNotDialable': 'Invalid Number',
      'InvalidPhoneNumber': 'Invalid Number',
      'InvalidEmailAddress': 'Invalid Email',
      'InvalidContactFlow': 'Invalid Flow',
      'InvalidLambdaResponse': 'Lambda Error',
      'RequestTimedOut': 'Timeout',
      'Timeout': 'Timeout',
      'ExceptionThrown': 'Exception',
      'Error': 'Error',
      'InputTimeLimitExceeded': 'Timeout'
    };
    return errorLabels[errorType] || errorType;
  }

  /**
   * Get condition label for display
   */
  static getConditionLabel(condition, index, actionType = null) {
    if (!condition.Condition) return `Branch ${index + 1}`;
    
    const { Operator, Operands } = condition.Condition;
    
    // Special handling for CheckHoursOfOperation
    if (actionType === 'CheckHoursOfOperation' && Operator === 'Equals' && Operands && Operands.length > 0) {
      if (Operands[0] === 'True') {
        return 'In Hours';
      }
      if (Operands[0] === 'False') {
        return 'Out of Hours';
      }
    }

    if (actionType === 'GetParticipantInput' && Operator === 'Equals' && Operands && Operands.length > 0) {
      const input = Operands[0];
      // If it's a number input, show it clearly
      if (/^\d+$/.test(input)) {
        return `Press ${input}`;
      }
      return `"${input}"`;
    }
    
    if (Operator === 'Equals' && Operands && Operands.length > 0) {
      return `= "${Operands[0]}"`;
    }
    
    if (Operator === 'NumberLessThan' && Operands && Operands.length > 0) {
      return `< ${Operands[0]}`;
    }
    
    if (Operator === 'NumberGreaterThan' && Operands && Operands.length > 0) {
      return `> ${Operands[0]}`;
    }
    
    if (Operator === 'NumberEqualTo' && Operands && Operands.length > 0) {
      return `= ${Operands[0]}`;
    }
    
    if (Operands && Operands.length > 0) {
      return `${Operator}: ${Operands.join(', ')}`;
    }
    
    return Operator || `Branch ${index + 1}`;
  }

  /**
   * Get block category for styling
   */
  static getBlockCategory(action) {
    const categoryMap = {
      'MessageParticipant': 'communication',
      'MessageParticipantIteratively': 'communication',
      'GetParticipantInput': 'input',
      'InvokeLambdaFunction': 'integration',
      'ConnectParticipantWithLexBot': 'input',
      'UpdateContactTargetQueue': 'routing',
      'TransferContactToQueue': 'routing',
      'TransferToFlow': 'routing',
      'UpdateContactRoutingBehavior': 'routing',
      'SetWorkingQueue': 'routing',
      'ChangeRoutingPriority': 'routing',
      'CheckHoursOfOperation': 'validation',
      'CheckMetricData': 'validation',
      'Compare': 'validation',
      'DisconnectParticipant': 'terminator',
      'EndFlowExecution': 'terminator',
      'UpdateContactRecordingBehavior': 'recording',
      'UpdateContactAttributes': 'data',
      'GetAttributes': 'data',
      'StoreCustomerInput': 'data',
      'TransferParticipantToThirdParty': 'transfer',
      'CreateCallbackContact': 'callback',
      'UpdateContactCallbackNumber': 'callback',
      'UpdateContactTextToSpeechVoice': 'communication',
      'UpdatePreviousContactParticipantState': 'communication',
      'ShowView': 'communication',
      'Hold': 'communication',
      'Resume': 'communication',
      'UpdateContactEventHooks': 'system',
      'UpdateFlowLoggingBehavior': 'system',
      'Wait': 'system',
      'WaitForEvent': 'system',
      'Loop': 'system'
    };
    
    return categoryMap[action.Type] || 'general';
  }

  /**
   * Get action icon
   */
  static getActionIcon(action) {
    const icons = {
      'MessageParticipant': '💬',
      'GetParticipantInput': '⌨️',
      'InvokeLambdaFunction': '🔗',
      'UpdateContactTargetQueue': '🚥',
      'TransferContactToQueue': '📞',
      'TransferToFlow': '🔄',
      'CheckHoursOfOperation': '🕐',
      'CheckMetricData': '📊',
      'DisconnectParticipant': '📵',
      'UpdateContactRecordingBehavior': '🎙️',
      'UpdateContactAttributes': '📊',
      'TransferParticipantToThirdParty': '📞',
      'Compare': '⚖️',
      'CreateCallbackContact': '📲',
      'UpdateContactCallbackNumber': '📱',
      'UpdateContactRoutingBehavior': '🔀',
      'ShowView': '👁️',
      'Wait': '⏳',
      'MessageParticipantIteratively': '🔁',
      'Loop': '🔁',
      'InvokeFlowModule': '📁',
      'CompleteOutboundCall': '📞'
    };
    
    return icons[action.Type] || '⚙️';
  }

  /**
   * Get action description
   */
  static getActionDescription(action) {
    const descriptions = {
      'MessageParticipant': 'Plays a message to the participant',
      'GetParticipantInput': 'Collects input from the participant',
      'InvokeLambdaFunction': 'Invokes an AWS Lambda function',
      'UpdateContactTargetQueue': 'Sets the queue for the contact',
      'TransferContactToQueue': 'Transfers the contact to a queue',
      'TransferToFlow': 'Transfers to another contact flow',
      'CheckHoursOfOperation': 'Checks if within operating hours',
      'CheckMetricData': 'Checks metric data and branches based on conditions',
      'DisconnectParticipant': 'Ends the contact',
      'UpdateContactRecordingBehavior': 'Controls call recording',
      'UpdateContactAttributes': 'Sets contact attributes',
      'TransferParticipantToThirdParty': 'Transfers to external number',
      'Compare': 'Compares a value and branches based on conditions',
      'UpdateContactRoutingBehavior': 'Updates contact routing priority or time adjustment',
      'ShowView': 'Displays a view to the participant',
      'Wait': 'Waits for a specified duration',
      'MessageParticipantIteratively': 'Plays multiple messages in a loop',
      'Loop': 'Loop Number of Times'
    };
    
    return descriptions[action.Type] || `Performs ${action.Type} action`;
  }

  /**
   * Get block parameters for specific action types
   */
  static getBlockParameters(action) {
    if (!action.Parameters) return {};

    switch (action.Type) {
      case 'MessageParticipant':
        return {
          text: action.Parameters.Text || '',
          textType: action.Parameters.TextType || 'text'
        };
      case 'GetParticipantInput':
        return {
          text: action.Parameters.Text || '',
          timeout: action.Parameters.InputTimeLimitSeconds || 5,
          storeInput: action.Parameters.StoreInput || false
        };
      case 'InvokeLambdaFunction':
        return {
          functionArn: action.Parameters.LambdaFunctionARN || '',
          timeout: action.Parameters.InvocationTimeLimitSeconds || 8
        };
      case 'Compare':
        return {
          comparisonValue: action.Parameters.ComparisonValue || ''
        };
      default:
        return action.Parameters;
    }
  }

  /**
   * Get detailed display information for specific node types
   */
  static getDisplayInfo(action, exportedData, instanceId) {
    const info = {};
    
    switch (action.Type) {
      case 'Compare':
        info.comparisonValue = action.Parameters?.ComparisonValue || '';
        info.conditions = this.getCompareConditions(action);
        break;
      case 'MessageParticipant':
        if (action.Parameters?.Text) {
          info.fullMessage = action.Parameters.Text;
          info.displayMessage = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId, exportedData, instanceId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        } else if (action.Parameters?.Media?.Uri) {
          info.mediaUri = action.Parameters.Media.Uri;
          info.contentType = 'media';
        }
        break;
      case 'GetParticipantInput':
        if (action.Parameters?.Text) {
          info.fullPrompt = action.Parameters.Text;
          info.displayPrompt = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId, exportedData, instanceId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        } else if (action.Parameters?.Media?.Uri) {
          info.mediaUri = action.Parameters.Media.Uri;
          info.contentType = 'media';
        }
        
        info.timeout = action.Parameters?.InputTimeLimitSeconds || 5;
        info.storeInput = action.Parameters?.StoreInput === 'True' || action.Parameters?.StoreInput === true;
        break;
      case 'ConnectParticipantWithLexBot':
        // Handle prompt display similar to GetParticipantInput
        if (action.Parameters?.Text) {
          info.fullPrompt = action.Parameters.Text;
          info.displayPrompt = this.truncateText(action.Parameters.Text, 200);
          info.contentType = 'text';
        } else if (action.Parameters?.SSML) {
          info.fullSSML = action.Parameters.SSML;
          info.displaySSML = this.truncateText(action.Parameters.SSML, 200);
          info.contentType = 'ssml';
        } else if (action.Parameters?.PromptId) {
          info.promptName = this.lookupPromptName(action.Parameters.PromptId, exportedData, instanceId);
          info.promptId = action.Parameters.PromptId;
          info.contentType = 'prompt';
        }
        
        // Handle Lex Bot information
        if (action.Parameters?.LexV2Bot?.AliasArn) {
          info.lexBotInfo = this.lookupLexV2BotName(action.Parameters.LexV2Bot.AliasArn, exportedData, instanceId);
          info.lexBotType = 'v2';
        } else if (action.Parameters?.LexBot?.Name && action.Parameters?.LexBot?.Alias) {
          info.lexBotInfo = {
            name: action.Parameters.LexBot.Name,
            alias: action.Parameters.LexBot.Alias,
            region: action.Parameters.LexBot.Region || 'Unknown'
          };
          info.lexBotType = 'v1';
        }
        
        info.timeout = action.Parameters?.InputTimeLimitSeconds || 5;
        break;
      case 'UpdateContactTextToSpeechVoice':
        if (action.Parameters?.TextToSpeechVoice) {
          info.textToSpeechVoice = action.Parameters.TextToSpeechVoice;
        }
        if (action.Parameters?.TextToSpeechEngine) {
          info.textToSpeechEngine = action.Parameters.TextToSpeechEngine;
        }
        if (action.Parameters?.TextToSpeechStyle) {
          info.textToSpeechStyle = action.Parameters.TextToSpeechStyle;
        }
        break;
      case 'TransferToFlow':
        info.targetFlowName = this.lookupFlowName(action.Parameters?.ContactFlowId, exportedData, instanceId);
        info.targetFlowId = action.Parameters?.ContactFlowId;
        break;
      case 'UpdateContactTargetQueue':
        // Handle both QueueId and AgentId parameters
        const targetId = action.Parameters?.QueueId || action.Parameters?.AgentId;
        
        if (targetId) {
          // Check if it's a JSONPath expression
          if (targetId.startsWith('$.')) {
            info.queueName = targetId; // Display JSONPath as-is
            info.isJsonPath = true;
          } else if (action.Parameters?.AgentId || (targetId && targetId.includes('/agent/'))) {
            // It's an agent target
            info.queueName = this.lookupUserName(targetId, exportedData, instanceId);
            info.isAgentTarget = true;
          } else {
            // Regular queue lookup
            info.queueName = this.lookupQueueName(targetId, exportedData, instanceId);
            info.isAgentTarget = false;
          }
        } else {
          info.queueName = 'Unknown Queue';
        }
        
        info.queueId = action.Parameters?.QueueId;
        info.agentId = action.Parameters?.AgentId;
        break;
      case 'CheckHoursOfOperation':
        if (action.Parameters?.HoursOfOperationId) {
          info.hoursName = this.lookupHoursName(action.Parameters.HoursOfOperationId, exportedData, instanceId);
          info.hoursId = action.Parameters.HoursOfOperationId;
        } else {
          info.hoursName = 'Current Queue';
        }
        break;
      case 'CheckMetricData':
        // Extract metric type information
        if (action.Parameters?.MetricType) {
          info.metricType = action.Parameters.MetricType;
        }
        
        // Handle Queue ID if present
        if (action.Parameters?.QueueId) {
          info.queueName = this.lookupQueueName(action.Parameters.QueueId, exportedData, instanceId);
          info.queueId = action.Parameters.QueueId;
        }
        
        // Handle Agent ID if present
        if (action.Parameters?.AgentId) {
          info.agentName = this.lookupUserName(action.Parameters.AgentId, exportedData, instanceId);
          info.agentId = action.Parameters.AgentId;
        }
        
        // Other metric parameters for display
        if (action.Parameters?.Statistic) {
          info.statistic = action.Parameters.Statistic;
        }
        if (action.Parameters?.Threshold) {
          info.threshold = action.Parameters.Threshold;
        }
        if (action.Parameters?.Operator) {
          info.operator = action.Parameters.Operator;
        }
        break;
      case 'TransferParticipantToThirdParty':
        info.phoneNumber = action.Parameters?.ThirdPartyPhoneNumber || '';
        info.timeout = action.Parameters?.ThirdPartyConnectionTimeLimitSeconds || 30;
        break;
      case 'InvokeLambdaFunction':
        info.functionName = this.extractLambdaName(action.Parameters?.LambdaFunctionARN);
        info.functionArn = action.Parameters?.LambdaFunctionARN;
        info.timeout = action.Parameters?.InvocationTimeLimitSeconds || 8;
        
        // Add Lambda invocation attributes if they exist
        if (action.Parameters?.LambdaInvocationAttributes) {
          info.lambdaInvocationAttributes = action.Parameters.LambdaInvocationAttributes;
          info.attributeCount = Object.keys(action.Parameters.LambdaInvocationAttributes).length;
        }
        break;
      case 'UpdateContactAttributes':
        info.attributeCount = action.Parameters?.Attributes ? Object.keys(action.Parameters.Attributes).length : 0;
        info.attributes = action.Parameters?.Attributes || {};
        break;
      case 'UpdateContactRecordingBehavior':
        info.recordingParticipants = action.Parameters?.RecordingBehavior?.RecordedParticipants || [];
        break;
      case 'CreateCallbackContact':
        if (action.Parameters?.QueueId) {
        info.targetQueue = this.lookupQueueName(action.Parameters?.QueueId, exportedData, instanceId);
        } else {
          info.targetQueue = 'Current Queue';
        }
        info.initialDelay = action.Parameters?.InitialCallDelaySeconds || 0;
        info.maxAttempts = action.Parameters?.MaximumConnectionAttempts || 1;
        break;
      case 'UpdateContactEventHooks':
        if (action.Parameters?.EventHooks) {
          const eventHooks = action.Parameters.EventHooks;
          const hookType = Object.keys(eventHooks)[0]; // Get the first (and likely only) hook type
          const hookValue = eventHooks[hookType];
          
          info.eventHookType = hookType;
          info.eventHookValue = hookValue;
          
          // Look up names based on hook type and ARN type
          if (['AgentHold', 'AgentWhisper', 'CustomerHold', 'CustomerRemaining', 'CustomerWhisper'].includes(hookType)) {
            // These use flow ARNs
            info.eventHookTargetName = this.lookupFlowName(hookValue, exportedData, instanceId);
          } else if (hookType === 'CustomerQueue') {
            // CustomerQueue can use either flow ARN or queue ARN - check which one
            if (hookValue && hookValue.includes('/queue/')) {
              info.eventHookTargetName = this.lookupQueueName(hookValue, exportedData, instanceId);
            } else if (hookValue && hookValue.includes('/contact-flow/')) {
              info.eventHookTargetName = this.lookupFlowName(hookValue, exportedData, instanceId);
            }
          }
          // For other hook types (DefaultAgentUI, DisconnectAgentUI, PauseContact, ResumeContact),
          // we don't need to look up names as they don't reference external resources
        }
        break;
      case 'UpdateContactRoutingBehavior':
        if (action.Parameters?.QueuePriority !== undefined) {
          info.queuePriority = action.Parameters.QueuePriority;
          info.routingType = 'priority';
        } else if (action.Parameters?.QueueTimeAdjustmentSeconds !== undefined) {
          info.queueTimeAdjustment = action.Parameters.QueueTimeAdjustmentSeconds;
          info.routingType = 'timeAdjustment';
        }
        break;

      case 'ShowView':
        if (action.Parameters?.ViewResource?.Id) {
          info.viewId = action.Parameters.ViewResource.Id;
          info.viewVersion = action.Parameters.ViewResource.Version;
        }
        info.timeout = action.Parameters?.InvocationTimeLimitSeconds || 400;
        break;

      case 'Wait':
        info.timeoutSeconds = action.Parameters?.TimeoutSeconds || 30;
        break;

      case 'Loop':
        info.loopCount = action.Parameters?.LoopCount || 1;
        break;

      case 'MessageParticipantIteratively':
        if (action.Parameters?.Messages && Array.isArray(action.Parameters.Messages)) {
          info.messages = action.Parameters.Messages.map((message, index) => {
            let content = '';
            let contentType = 'text';
            
            if (message.Text) {
              content = message.Text;
              contentType = 'text';
            } else if (message.SSML) {
              content = message.SSML;
              contentType = 'ssml';
            } else if (message.PromptId) {
              content = this.lookupPromptName(message.PromptId, exportedData, instanceId);
              contentType = 'prompt';
            } else if (message.Media?.Uri) {
              content = message.Media.Uri;
              contentType = 'media';
            }
            
            return {
              index: index + 1,
              content: this.truncateText(content, 100),
              fullContent: content,
              contentType
            };
          });
          info.messageCount = info.messages.length;
        }
        break;
      case 'UpdatePreviousContactParticipantState':
        if (action.Parameters?.PreviousContactParticipantState) {
          const state = action.Parameters.PreviousContactParticipantState;
          info.participantState = state;
          
          // Map the state to display text
          const stateDisplayMap = {
            'AgentOnHold': 'Hold Agent',
            'CustomerOnHold': 'Hold Customer', 
            'OffHold': 'Conference All'
          };
          
          info.displayState = stateDisplayMap[state] || state;
        }
        break;
      case 'InvokeFlowModule':
        info.flowModuleName = this.lookupFlowModuleName(action.Parameters?.FlowModuleId, exportedData, instanceId);
        info.flowModuleId = action.Parameters?.FlowModuleId;
        break;
      case 'CompleteOutboundCall':
        if (action.Parameters?.CallerId?.Number) {
          info.phoneNumber = action.Parameters.CallerId.Number;
        }
        break;
      default:
        return info;
    }
    
    return info;
  }

  /**
   * Get conditions for Compare nodes with better formatting
   */
  static getCompareConditions(action) {
    if (!action.Transitions?.Conditions) return [];
    
    return action.Transitions.Conditions.map((condition, index) => ({
      label: this.getConditionLabel(condition, index),
      operator: condition.Condition?.Operator || '',
      operands: condition.Condition?.Operands || [],
      nextAction: condition.NextAction
    }));
  }

  /**
   * Get action label for display
   */
  static getActionLabel(action, exportedData, instanceId) {
    const displayInfo = this.getDisplayInfo(action, exportedData, instanceId);
    
    switch (action.Type) {
      case 'Compare':
        return `Compare: ${displayInfo.comparisonValue || 'Value'}`;
      
      case 'MessageParticipant':
        return 'Play Message';
      
      case 'GetParticipantInput':
        return 'Get Input';

      case 'ConnectParticipantWithLexBot':
        if (displayInfo.lexBotInfo) {
          if (displayInfo.lexBotType === 'v2') {
            return `Lex Bot: ${displayInfo.lexBotInfo.name || 'Unknown'} (${displayInfo.lexBotInfo.alias || 'Unknown'})`;
          } else {
            return `Lex Bot: ${displayInfo.lexBotInfo.name} (${displayInfo.lexBotInfo.alias})`;
          }
        }
        return 'Connect to Lex Bot';
      
      case 'TransferToFlow':
        return displayInfo.targetFlowName || 'Transfer to Flow';
      
      case 'UpdateContactTargetQueue':
        return displayInfo.queueName || 'Set Queue';
      
      case 'CheckHoursOfOperation':
        return displayInfo.hoursName || 'Check Hours';
      
      case 'CheckMetricData':
        if (displayInfo.metricType) {
          return `Check ${displayInfo.metricType}`;
        }
        return 'Check Metrics';
      
      case 'TransferParticipantToThirdParty':
        return `External Transfer: ${displayInfo.phoneNumber || 'Phone'}`;
      
      case 'InvokeLambdaFunction':
        return `Lambda: ${displayInfo.functionName || 'Function'}`;
      
      case 'UpdateContactAttributes':
        return `Set ${displayInfo.attributeCount} Attribute${displayInfo.attributeCount !== 1 ? 's' : ''}`;
      
      case 'UpdateContactRecordingBehavior':
        const participants = displayInfo.recordingParticipants;
        if (participants.length === 0) {
          return 'Stop Recording';
        } else if (participants.includes('Agent') && participants.includes('Customer')) {
          return 'Record All';
        } else if (participants.includes('Agent')) {
          return 'Record Agent';
        } else if (participants.includes('Customer')) {
          return 'Record Customer';
        }
        return 'Update Recording';
      
      case 'CreateCallbackContact':
        return `Callback: ${displayInfo.targetQueue || 'Queue'}`;
      case 'UpdateContactEventHooks':
        if (displayInfo.eventHookType) {
          const hookType = displayInfo.eventHookType;
          const hookLabels = {
            'AgentHold': 'Set Agent Hold Flow',
            'AgentWhisper': 'Set Agent Whisper Flow',
            'CustomerHold': 'Set Customer Hold Flow',
            'CustomerQueue': 'Set Customer Queue Flow',
            'CustomerRemaining': 'Set Disconnect Flow',
            'CustomerWhisper': 'Set Customer Whisper Flow',
            'DefaultAgentUI': 'Default flow for Agent UI',
            'DisconnectAgentUI': 'Disconnect flow for Agent UI',
            'PauseContact': 'Flow at Contact Pause',
            'ResumeContact': 'Flow at Contact Resume'
          };
          
          const baseLabel = hookLabels[hookType] || 'Set Event Hooks';
          
          // Add the target name if available and relevant
          if (displayInfo.eventHookTargetName && 
              ['AgentHold', 'AgentWhisper', 'CustomerHold', 'CustomerRemaining', 'CustomerWhisper', 'CustomerQueue'].includes(hookType)) {
            return `${baseLabel}: ${displayInfo.eventHookTargetName}`;
          } else {
            return baseLabel;
          }
        }
        return 'Set Event Hooks'; 
      case 'UpdateContactRoutingBehavior':
        if (displayInfo.routingType === 'priority') {
          return `Set Priority: ${displayInfo.queuePriority}`;
        } else if (displayInfo.routingType === 'timeAdjustment') {
          return `Adjust Time: ${displayInfo.queueTimeAdjustment}s`;
        }
        return 'Update Routing';

      case 'ShowView':
        return displayInfo.viewId ? `Show View: ${displayInfo.viewId}` : 'Show View';

      case 'Wait':
        return `Wait: ${displayInfo.timeoutSeconds}s`;

      case 'Loop':
        return `Loop (${displayInfo.loopCount}x)`;

      case 'MessageParticipantIteratively':
        return displayInfo.messageCount > 0 ? 
          `Loop Messages (${displayInfo.messageCount})` : 
          'Loop Messages';

      case 'UpdatePreviousContactParticipantState':
        return displayInfo.displayState || 'Hold Customer or Agent';
      
      case 'InvokeFlowModule':
        return displayInfo.flowModuleName || 'Invoke Flow Module';
      
      case 'CompleteOutboundCall':
        return displayInfo.phoneNumber ? `Call: ${displayInfo.phoneNumber}` : 'Complete Outbound Call';
      
      default:
        return this.getOriginalActionLabel(action);
    }
  }

  /**
   * Get original action label for fallback
   */
  static getOriginalActionLabel(action) {
    const labels = {
      'UpdateFlowLoggingBehavior': 'Enable Logging',
      'DisconnectParticipant': 'Disconnect',
      'UpdateContactEventHooks': 'Set Event Hooks',
      'MessageParticipantIteratively': 'Loop Message',
      'EndFlowExecution': 'End Flow',
      'GetAttributes': 'Get Attributes',
      'WaitForEvent': 'Wait for Event',
      'SetWorkingQueue': 'Set Working Queue',
      'ChangeRoutingPriority': 'Change Priority',
      'Hold': 'Hold',
      'Resume': 'Resume',
      'ConnectParticipantWithLexBot': 'Lex Bot',
      'StoreCustomerInput': 'Store Input'
    };

    return labels[action.Type] || this.formatActionType(action.Type);
  }

  /**
   * Get action metadata
   */
  static getActionMetadata(action) {
    return {
      identifier: action.Identifier,
      type: action.Type,
      hasErrors: !!(action.Transitions?.Errors?.length),
      hasConditions: !!(action.Transitions?.Conditions?.length),
      errorCount: action.Transitions?.Errors?.length || 0,
      conditionCount: action.Transitions?.Conditions?.length || 0,
      parameters: this.getBlockParameters(action)
    };
  }

  /**
   * Get node type for React Flow/Lucid mapping
   */
  static getNodeType(action) {
    const decisionTypes = ['CheckHoursOfOperation', 'Compare', 'CheckMetricData'];
    const endTypes = ['DisconnectParticipant', 'EndFlowExecution'];
    
    if (endTypes.includes(action.Type)) {
      return 'endNode';
    }
    
    if (decisionTypes.includes(action.Type)) {
      return 'decisionNode';
    }
    
    return 'processNode';
  }

  /**
   * Check if action is a start action
   */
  static isStartAction(actionId) {
    return false; // Entry node is synthetic
  }

  /**
   * Check if action is an end action
   */
  static isEndAction(action) {
    if (!action) return false;
    
    return action.Type === 'DisconnectParticipant' || 
           action.Type === 'EndFlowExecution' ||
           !action.Transitions ||
           Object.keys(action.Transitions).length === 0;
  }
}